// components/SessionWarningModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Progress } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface SessionWarningModalProps {
    open: boolean;
    timeLeft: number;             // snapshot from parent (ms)
    onExtend: () => void;
    onLogout: () => void;
}

const WARNING_TIME =
    Number.parseInt(process.env.REACT_APP_SESSION_WARNING_MINUTES || '2') * 60 * 1000;

const SessionWarningModal = ({
    open,
    timeLeft,
    onExtend,
    onLogout,
}: SessionWarningModalProps) => {
    const [displayTime, setDisplayTime] = useState(timeLeft);
    const prevDisplayRef = useRef<number | null>(null);

    // When modal opens, set local timer
    useEffect(() => {
        if (open) {
            setDisplayTime(timeLeft);
            prevDisplayRef.current = null; // reset previous tracker on open
        }
    }, [open, timeLeft]);

    // Countdown inside modal
    useEffect(() => {
        if (!open) return;

        const timer = setInterval(() => {
            setDisplayTime(prev => Math.max(prev - 1000, 0));
        }, 1000);

        return () => clearInterval(timer);
    }, [open]);

    // AUTO-LOGOUT only when timer transitions from >0 to 0
    useEffect(() => {
        if (!open) {
            prevDisplayRef.current = null;
            return;
        }

        const prev = prevDisplayRef.current;

        // initialize prev on first run (don’t trigger logout if prev is null)
        if (prev === null) {
            prevDisplayRef.current = displayTime;
            return;
        }

        // trigger logout only when prev > 0 and now it's exactly 0
        if (prev > 0 && displayTime === 0) {
            // small delay so UI shows 0:00
            setTimeout(() => {
                onLogout();
            }, 300);
        }

        // update prev
        prevDisplayRef.current = displayTime;
    }, [displayTime, open, onLogout]);

    const minutes = Math.floor(displayTime / 60000);
    const seconds = Math.floor((displayTime % 60000) / 1000);
    const percent = Math.max(0, Math.min(100, Math.floor((displayTime / WARNING_TIME) * 100)));
    return (
        <Modal
            open={open}
            closable={false}
            maskClosable={false}
            width={300}
            centered
            style={{ padding: 0 }}
            footer={null}
            title={null}
            wrapProps={{ 'data-testid': 'session-warning-modal' }}
        >
            <div
                style={{
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    width: '100%',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
                    <span>Session Timeout Warning</span>
                </div>
                <p style={{ fontSize: 12, color: '#666', margin: 0, textAlign: 'center' }}>
                    You will be automatically logged out in:
                </p>

                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 'bold',
                        color: percent < 30 ? '#ff4d4f' : '#1890ff',
                        margin: 0,
                    }}
                    data-testid="session-countdown"
                    data-seconds-left={String(Math.floor(displayTime / 1000))}
                >
                    {minutes}:{seconds.toString().padStart(2, '0')}
                </div>

                <Progress
                    percent={percent}
                    strokeColor={percent < 30 ? '#ff4d4f' : '#1890ff'}
                    showInfo={false}
                    strokeWidth={6}
                    data-testid="session-progress"
                    data-percent={String(percent)}
                    style={{ width: '100%', margin: 0 }}
                />

                {/* Custom Footer */}
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button onClick={onLogout} size="small" style={{ padding: '2px 6px' }} data-testid="session-logout-button">
                        Logout
                    </Button>
                    <Button onClick={onExtend} type="primary" size="small" style={{ padding: '2px 6px' }} data-testid="session-continue-button">
                        Continue
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SessionWarningModal;